import { type Row } from './context.js';
import { containsAbilityTriggerKey } from '../compile/inventory-ability-grant-helpers.js';

/** Canonical status mechanics with no authored layer and no PHP movement lane. */
export const BROWSER_ONLY_SOLID_PASSAGE_STATUSES = ['incorporeal'] as const;

/**
 * OWNER RULING 2026-10-09 ("Fix 1": no new PHP rule copies). PHP interprets the database and serves saved state; every rule is
 * the front end's. A layer a class / subclass port (or any new mechanic) adds is read by the JS/TS engine ONLY — PHP janus() never
 * learns it. So a cast whose ability (or caster) carries one of these layers must never run on the legacy use_ability_inc.php path,
 * where PHP would resolve it without the effect: the page REFUSES it with a named reason, which keeps the gap visible until the
 * hand-back that sent it there is ported.
 *
 * Register every new layer here in the same change that adds its TS reader (.claude/skills/add-layer-type/SKILL.md Step 5).
 * Guard: tools/lint/php-rule-layer-lint.php fails any PHP rule file that names a layer listed here.
 */
export const BROWSER_ONLY_LAYERS: ReadonlySet<string> = new Set<string>([
  'contesting_roll_skipped_for_same_team_type',
  'effect_ignores_damage_type_resistance',
  'mana_cannot_be_reduced', 'charges_cannot_be_reduced',
  // Sanguine next-spell payloads (sanguine24); literals also feed the PHP no-rule-copy audit.
  'next_spell_aoe_changed_by_percentage', 'next_spell_aoe_effect_duplicated', 'next_spell_ap_override', 'next_spell_bounce_cast',
  'next_spell_concentration_uncapped', 'next_spell_damage_dice_changed_by_percentage', 'next_spell_damage_type_choice',
  'next_spell_hits_opposing_team_only', 'next_spell_paoe_applied_to_targets', 'next_spell_range_multiplier',
  'next_spell_self_range_becomes_reach', 'next_spell_target_opportunity_attack', 'next_spell_target_rider',
  'next_spell_twin_cast', 'next_spell_upcast_levels',
  // Wizard v9 (Geno, 2026-10-09). Left OUT on purpose: carrying_capacity_multiplier, speed_cannot_be_reduced and
  // immune_to_size_reduction are compile-only (no cast resolution reads them off the ability or caster), so listing them would refuse
  // every legacy-route cast by a Gravity / Transmutation wizard without protecting any effect. Their guard is the PHP rule-layer lint.
  // summons_gain_caster_cantrips is out too: it sits on the CONTROLLER, not on the summon's cast; ability-truth.ts / use-ability.ts
  // refuse the summon's granted-cantrip cast at the legacy route themselves (a marker row checked against the layer name).
  // crafting_waives_nonmagical_reagents is out as well: the craft-start lane (crafting-start.ts) refuses its own hand-back for a waiver
  // holder, and listing it here would refuse every other legacy-route cast by a Universal Crafter wizard.
  'spells_cannot_be_counterspelled',
  'spell_mana_discount',
  'next_spell_mana_discount',
  'aoe_spells_spare_allies',
  'scroll_spell_damage_multiplier',
  'damage_type_is_rolled_at_max',
  'healing_roll_reroll_1s_and_2s',
  'next_summon_ap_override',
  'triggers_when_caster_uses_corpse',
  'triggers_when_caster_gets_item_type_against',
  'uses_corpse',
  // Janus lane PC1 (2026-10-09) — PHP-copy retirement. ABILITY-held layers only, each on one menta that no trigger / reaction / status
  // re-dispatch carries (measured: tools/probes/pc1_layer_carrier_census_v1.php), so only a direct cast reaches janus() and that cast is now
  // refused on the legacy route; their PHP copies are deleted (board R-151 / R-172 / R-175). Caster- / target- / status-held layers whose PHP
  // copy also serves server-started dispatches, ticks, PHP damage application or the PHP compile stay OUT (registering them protects nothing
  // those paths reach and refuses every legacy cast by the holder) — see plans/janus-lane-pc1-php-copy-retirement-handoff-2026-10-09.md.
  'status_effect_applied_is_caster_choice',        // R-151 Override Emotion — the pick rides the lane (use-ability.ts statusChoice)
  'immune_to_ability_after_effect_until_combat_end', // R-172 Monstrous Transformation — the lockout row is written by combat-lockout.ts
  'chain_targets_max',                             // R-175 Chain Combustion (+ its three spec layers) — chain-leap.ts
  'chain_distance_ft',
  'chain_dice',
  'chain_die',
  // Food/drink meter changes are ability-held on the consumed item.
  'change_hunger', 'change_thirst',
  // PLANNER-PORTS-1020: native damage providers and peer damage/status ports with no retained PHP readers.
  'ignore_damage_mitigation_damage_type',
  'damage_to_objects_multiplier',
  'spells_ignore_damage_mitigation',
  'sleeping_not_ended_by_damage',
  'target_retries_contesting_roll_on_damage_taken',
  'damage_on_status_expiry',
  'damage_on_status_expiry_splash_ft',
  'damage_on_status_expiry_splash_dice',
  'damage_on_status_expiry_splash_die',
  'change_teleport_remaining',
  // Weapon-passive windows: native status projection, persisted attack budget and expiry.
  'all_damage_output_changed_by_percentage',
  'damage_heals_caster',
  'surface_type', 'surface_reaction',
  // Skill-roll status gates and consumption are native; their PHP copies are retired (T3a).
  'triggers_on_critical_hit', 'triggers_on_hit', 'triggers_on_miss', 'triggers_on_not_a_critical_hit',
  'triggers_on_next_roll_type', 'triggers_on_next_roll_type_against', 'triggers_on_next_roll_type_against_and_hits',
  'triggers_when_caster_makes_roll_type', 'triggers_when_caster_uses_item_type',
  'triggers_when_target_gets_item_type_against', 'triggers_when_target_makes_roll_type', 'triggers_when_target_uses_item_type',
  'triggers_when_target_enters_reach', 'triggers_when_target_moves', 'triggers_when_target_leaves_reach',
]);

// Innate extra_attack remains a shared compile input. Only its triggered grant
// requires the native status lifetime; legacy compile references are not new rules.
const BROWSER_ONLY_TRIGGERED_LAYERS = new Set(['extra_attack']);

/** Null = the legacy route may run. Otherwise the named refusal reason (`browser-only-layer:<layer>:legacy-route-refused:<why>`). */
export function browserOnlyLegacyRouteReason(rows: ReadonlyArray<Row | null | undefined>, value: (row: Row, key: string) => unknown,
  reason: string, layers: ReadonlySet<string> = BROWSER_ONLY_LAYERS): string | null {
  for (const row of rows) {
    if (!row) continue;
    for (const key of layers) if (value(row, key) != null) return `browser-only-layer:${key}:legacy-route-refused:${reason}`;
    if (containsAbilityTriggerKey(row)) for (const key of BROWSER_ONLY_TRIGGERED_LAYERS)
      if (value(row, key) != null) return `browser-only-layer:${key}:legacy-route-refused:${reason}`;
  }
  return null;
}
